import type { MDXRemoteSerializeResult } from 'next-mdx-remote'
import type { MutableRefObject, RefObject } from 'react'
import React from 'react'
import { useFragment } from 'gql'
import type { PetParentsCornerPageQuery } from 'gql/graphql'
import { pageSectionsFragment } from 'query/single-page/fragment'
import MDXBlock from '../../MDXBlock/MDXBlock'
import type { TAnchor } from '../../types'
import ArticlesPetParentsCornerBlock from '../ArticlesPetParentsCornerBlock/ArticlesPetParentsCornerBlock'
import ContestPetParentsCornerBlock from '../ContestPetParentsCornerBlock/ContestPetParentsCornerBlock'
import FaqPetParentsCornerBlock from '../FaqPetParentsCornerBlock/FaqPetParentsCornerBlock'
import FormStoryPetParentsCornerBlock from '../FormStoryPetParentsCornerBlock/FormStoryPetParentsCornerBlock'
import StoriesPetParentsCornerBlock from '../StoriesPetParentsCornerBlock/StoriesPetParentsCornerBlock'
import VideosPetParentsCornerBlock from '../VideosPetParentsCornerBlock/VideosPetParentsCornerBlock'

interface TPetParentsCornerBlockDispatcherProps {
  anchors: TAnchor[]
  anchorIndex: number
  section: NonNullable<
    NonNullable<NonNullable<PetParentsCornerPageQuery['pages'][number]['translations']>[number]>['sections']
  >[number]
  refs: MutableRefObject<RefObject<HTMLDivElement>[] | undefined>
  mdxSources: Array<MDXRemoteSerializeResult<Record<string, unknown>, Record<string, unknown>>>
  mdxBlockIndex: number
  frTranslations?: NonNullable<
    NonNullable<NonNullable<PetParentsCornerPageQuery['pages'][number]['translations']>[number]>['sections']
  >[number]
}

/* eslint-disable react/display-name */
const PetParentsCornerBlockDispatcher = (props: TPetParentsCornerBlockDispatcherProps) => {
  const { anchorIndex, section, refs, mdxSources, mdxBlockIndex, frTranslations } = props

  const typedSection = useFragment(pageSectionsFragment, section)
  const typedFrSection = useFragment(pageSectionsFragment, frTranslations)

  switch (typedSection?.item?.type) {
    case 'block_faq':
      return (
        <FaqPetParentsCornerBlock
          ref={refs.current?.[anchorIndex]}
          frItem={typedFrSection?.item}
          {...typedSection.item}
        />
      )
    case 'block_mdx':
      if (typedSection.item.isAnchored) {
        return <MDXBlock ref={refs.current?.[anchorIndex]} {...typedSection.item} source={mdxSources[mdxBlockIndex]} />
      }
      if (typedSection.item.friendlyId === 'form-story') {
        return <FormStoryPetParentsCornerBlock {...typedSection.item} source={mdxSources[mdxBlockIndex]} />
      }
      return <MDXBlock {...typedSection.item} source={mdxSources[mdxBlockIndex]} />

    case 'block_content':
      switch (typedSection.item.friendlyId) {
        case 'recent-articles':
          return <ArticlesPetParentsCornerBlock ref={refs.current?.[anchorIndex]} {...typedSection.item} />
        case 'videos':
          return <VideosPetParentsCornerBlock ref={refs.current?.[anchorIndex]} {...typedSection.item} />
        default:
          return null
      }
    case 'block_stories':
      return <StoriesPetParentsCornerBlock ref={refs.current?.[anchorIndex]} {...typedSection.item} />
    case 'block_contest':
      return <ContestPetParentsCornerBlock ref={refs.current?.[anchorIndex]} {...typedSection.item} />
    default:
      return null
  }
}

export default PetParentsCornerBlockDispatcher
